import React, { useRef, useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';
import { CURRENCIES } from '../../types';

/**
 * Global currency selector. State lives in CurrencyContext, so every instance
 * (the /app header and the Coin Detail header) shares the same selection — there
 * is no local currency copy.
 */
export function CurrencySelector() {
  const { currency, setCurrencyCode, ratesLoading } = useCurrency();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={ratesLoading}
        className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
      >
        <span>{currency.code}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1.5 w-52 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-slate-700">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Currency</p>
          </div>
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              onClick={() => { setCurrencyCode(c.code); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors hover:bg-slate-700 ${currency.code === c.code ? 'text-slate-100 font-semibold' : 'text-slate-200'}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="font-semibold w-8">{c.code}</span>
                <span className="text-slate-400 text-xs">{c.name}</span>
              </div>
              {currency.code === c.code && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
